import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { AiModel } from '../types/data';
import { fetchAiModels, addAiModel, removeAiModel } from '../services/dataService';
import { aiService } from '../services/aiService';

const AiModels = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [models, setModels] = useState<AiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState<
    { id: string; supported_parameters: string[] }[]
  >([]);
  const [availableError, setAvailableError] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [selectedModel, setSelectedModel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setModels(await fetchAiModels());
    } catch (error) {
      console.error('Error loading AI models:', error);
      showNotification(t('ai_models.load_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OpenRouter's catalog is public/keyless, so no company key is needed.
  useEffect(() => {
    let active = true;
    (async () => {
      setFetching(true);
      setAvailableError(false);
      try {
        const { data, error } = await aiService.listModels();
        if (error || !data?.models) {
          throw error ?? new Error('No models returned');
        }
        if (active) {
          setAvailable(
            data.models.map((m) => ({
              id: m.id,
              supported_parameters: m.supported_parameters,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching available models:', error);
        if (active) setAvailableError(true);
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const vetted = new Set(models.map((m) => m.model));
  const availableToAdd = available.filter((m) => !vetted.has(m.id));

  useEffect(() => {
    if (!availableToAdd.some((m) => m.id === selectedModel)) {
      setSelectedModel(availableToAdd[0]?.id ?? '');
    }
  }, [availableToAdd, selectedModel]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedModel || saving) {
      return;
    }
    setSaving(true);
    try {
      const selected = available.find((m) => m.id === selectedModel);
      await addAiModel(selectedModel, newLabel, selected?.supported_parameters ?? null);
      setNewLabel('');
      showNotification(t('ai_models.added'), 'success');
      await load();
    } catch (error) {
      console.error('Error adding AI model:', error);
      showNotification(t('ai_models.add_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (model: string) => {
    try {
      await removeAiModel(model);
      showNotification(t('ai_models.removed'), 'success');
      await load();
    } catch (error) {
      console.error('Error removing AI model:', error);
      showNotification(t('ai_models.remove_error'), 'error');
    }
  };

  const inputClass =
    'block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm ' +
    'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  const renderAddForm = () => {
    if (availableError) {
      return <p className="text-sm text-red-600">{t('ai_models.fetch_error')}</p>;
    }
    return (
      <form onSubmit={handleAdd} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="model" className="block text-sm font-medium text-gray-700">
            {t('ai_models.model_id')}
          </label>
          <select
            id="model"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className={`${inputClass} mt-1 font-mono`}
            disabled={fetching || availableToAdd.length === 0}
          >
            {availableToAdd.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="label" className="block text-sm font-medium text-gray-700">
            {t('ai_models.label')}
          </label>
          <input
            id="label"
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={t('ai_models.label_placeholder')}
            className={`${inputClass} mt-1`}
          />
        </div>
        <button
          type="submit"
          disabled={saving || !selectedModel}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('ai_models.add')}
        </button>
      </form>
    );
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('ai_models.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('ai_models.subtitle')}</p>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        {fetching ? (
          <p className="text-sm text-gray-500">{t('ai_models.loading_available')}</p>
        ) : availableToAdd.length === 0 && !availableError ? (
          <p className="text-sm text-gray-500">{t('ai_models.all_added')}</p>
        ) : (
          renderAddForm()
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-4 text-sm text-gray-500">{t('common.loading')}</p>
        ) : models.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">{t('ai_models.empty')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {models.map((m) => (
              <li
                key={m.model}
                data-testid={`model-row-${m.model}`}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <span className="font-mono text-sm text-gray-900">{m.model}</span>
                  {m.label && <span className="ml-2 text-sm text-gray-500">{m.label}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(m.model)}
                  aria-label={`${t('ai_models.remove')} ${m.model}`}
                  className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  {t('ai_models.remove')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AiModels;
